<script setup>
import { ref, computed, onMounted } from 'vue';
import * as Sentry from '@sentry/vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useFunctionGetter, useStore } from 'dashboard/composables/store';

import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import LixfollowConnectionFields from './LixfollowConnectionFields.vue';
import lixfollowAPI from '../../../../api/integrations/lixfollow';

const { t } = useI18n();
const store = useStore();

const isLoading = ref(true);
const isSaving = ref(false);
const isTesting = ref(false);
// One of: null (not yet tested), 'ok', 'unreachable', 'bearer_missing',
// 'bearer_invalid', 'account_not_connected'. Mirrors the LixFollow `/ping`
// response.
const connectionStatus = ref(null);

const apiUrl = ref('');
const lixfollowAccessToken = ref('');
const hasSavedAccessToken = ref(false);
const chatwootAccountId = ref('');
const handshakeStatus = ref(null);
const isConfigured = ref(false);
const configDialogRef = ref(null);

const integrationLogoLightPath = '/dashboard/images/integrations/lixfollow.png';
const integrationLogoDarkPath =
  '/dashboard/images/integrations/lixfollow-dark.png';

const integration = useFunctionGetter(
  'integrations/getIntegration',
  'lixfollow'
);

const connectionState = computed(() => {
  if (isTesting.value) return 'checking';
  return connectionStatus.value ?? 'unknown';
});

// Single source of truth per connection state: i18n label suffix, colour tone,
// and optional hint suffix. Tone maps to badge/dot/text classes below.
const CONNECTION_META = {
  ok: { label: 'CONNECTED', tone: 'green', hint: null },
  unreachable: { label: 'UNREACHABLE', tone: 'red', hint: 'UNREACHABLE' },
  bearer_missing: {
    label: 'BEARER_MISSING',
    tone: 'amber',
    hint: 'BEARER_MISSING',
  },
  bearer_invalid: {
    label: 'BEARER_INVALID',
    tone: 'red',
    hint: 'BEARER_INVALID',
  },
  account_not_connected: {
    label: 'ACCOUNT_NOT_CONNECTED',
    tone: 'amber',
    hint: 'ACCOUNT_NOT_CONNECTED',
  },
  checking: { label: 'CHECKING', tone: 'checking', hint: null },
  unknown: { label: 'UNKNOWN', tone: 'unknown', hint: null },
};

const connectionMeta = computed(
  () => CONNECTION_META[connectionState.value] ?? CONNECTION_META.unknown
);

const connectionLabel = computed(() =>
  t(`INTEGRATION_SETTINGS.LIXFOLLOW.CONNECTION.${connectionMeta.value.label}`)
);

const connectionHint = computed(() => {
  const { hint } = connectionMeta.value;
  return hint
    ? t(`INTEGRATION_SETTINGS.LIXFOLLOW.CONNECTION.HINT.${hint}`)
    : '';
});

const connectionBadgeClass = computed(() => {
  const map = {
    green:
      'bg-n-teal-3 border-n-teal-5 dark:bg-n-teal-9/20 dark:border-n-teal-8',
    red: 'bg-n-ruby-3 border-n-ruby-5 dark:bg-n-ruby-9/20 dark:border-n-ruby-8',
    amber:
      'bg-n-amber-3 border-n-amber-5 dark:bg-n-amber-9/20 dark:border-n-amber-8',
    checking: 'bg-n-alpha-2 border-n-brand/40',
    unknown: 'bg-n-alpha-2 border-n-weak',
  };
  return map[connectionMeta.value.tone];
});

const connectionDotClass = computed(() => {
  const map = {
    green: 'bg-n-teal-9',
    red: 'bg-n-ruby-9',
    amber: 'bg-n-amber-9',
    checking: 'bg-n-brand animate-pulse',
    unknown: 'bg-n-slate-400',
  };
  return map[connectionMeta.value.tone];
});

const connectionTextClass = computed(() => {
  const map = {
    green: 'text-n-teal-11',
    red: 'text-n-ruby-11',
    amber: 'text-n-amber-11',
    checking: 'text-n-brand',
    unknown: 'text-n-slate-11',
  };
  return map[connectionMeta.value.tone];
});

const hasSavedConfiguration = settings =>
  Boolean(settings?.api_url?.trim()) && Boolean(settings?.access_token_present);

const testConnection = async () => {
  isTesting.value = true;
  try {
    const { data } = await lixfollowAPI.testConnection();
    connectionStatus.value = data?.status ?? 'unreachable';
  } catch (error) {
    Sentry.captureException(error);
    connectionStatus.value = 'unreachable';
  } finally {
    isTesting.value = false;
  }
};

const loadSettings = async () => {
  isLoading.value = true;
  try {
    const { data } = await lixfollowAPI.show();
    apiUrl.value = data?.api_url ?? '';
    // The server never returns the raw token — only whether one is saved.
    // Leave the field empty; an empty field on save means "keep existing".
    hasSavedAccessToken.value = Boolean(data?.access_token_present);
    lixfollowAccessToken.value = '';
    chatwootAccountId.value = data?.chatwoot_account_id ?? '';
    isConfigured.value = hasSavedConfiguration(data || {});
  } catch (error) {
    Sentry.captureException(error);
    isConfigured.value = false;
  } finally {
    isLoading.value = false;
  }
  if (isConfigured.value) await testConnection();
};

const saveSettings = async ({ closeDialog = false } = {}) => {
  isSaving.value = true;
  try {
    const payload = { api_url: apiUrl.value };
    if (lixfollowAccessToken.value?.trim()) {
      payload.lixfollow_access_token = lixfollowAccessToken.value.trim();
    }
    const { data } = await lixfollowAPI.update(payload);
    await store.dispatch('integrations/get', 'lixfollow');
    // handshake only comes back on update; re-read everything else from server.
    handshakeStatus.value = data?.handshake ?? null;
    await loadSettings();
    if (closeDialog && isConfigured.value) {
      configDialogRef.value?.close();
    }
    if (handshakeStatus.value && handshakeStatus.value.ok === false) {
      useAlert(
        t('INTEGRATION_SETTINGS.LIXFOLLOW.HANDSHAKE_ERROR', {
          reason: handshakeStatus.value.reason,
        })
      );
    } else {
      useAlert(t('INTEGRATION_SETTINGS.LIXFOLLOW.SAVE_SUCCESS'));
    }
  } catch (error) {
    Sentry.captureException(error);
    useAlert(t('INTEGRATION_SETTINGS.LIXFOLLOW.SAVE_ERROR'));
  } finally {
    isSaving.value = false;
  }
};

const openConfigDialog = () => configDialogRef.value?.open();

const saveSettingsFromDialog = () => saveSettings({ closeDialog: true });

onMounted(async () => {
  await store.dispatch('integrations/get', 'lixfollow');
  await loadSettings();
});
</script>

<template>
  <SettingsLayout :is-loading="isLoading">
    <template #header>
      <BaseSettingsHeader
        :title="$t('INTEGRATION_SETTINGS.LIXFOLLOW.TITLE')"
        :description="$t('INTEGRATION_SETTINGS.LIXFOLLOW.DESCRIPTION')"
        :back-button-label="$t('INTEGRATION_SETTINGS.HEADER')"
      />
    </template>

    <template #body>
      <div class="flex flex-col gap-6">
        <!-- Integration banner: logo + name + Connect button (only when not yet configured) -->
        <div
          class="flex flex-col items-start justify-between lg:flex-row lg:items-center p-6 outline outline-n-container outline-1 bg-n-card rounded-xl gap-6"
        >
          <div
            class="flex items-start lg:items-center justify-start flex-1 m-0 gap-6 flex-col lg:flex-row"
          >
            <div
              class="flex h-16 w-16 items-center justify-center flex-shrink-0"
            >
              <img
                :src="integrationLogoLightPath"
                :alt="$t('INTEGRATION_SETTINGS.LIXFOLLOW.TITLE')"
                class="max-w-full rounded-md border border-n-weak shadow-sm block dark:hidden bg-n-alpha-3 dark:bg-n-alpha-2"
              />
              <img
                :src="integrationLogoDarkPath"
                :alt="$t('INTEGRATION_SETTINGS.LIXFOLLOW.TITLE')"
                class="max-w-full rounded-md border border-n-weak shadow-sm hidden dark:block bg-n-alpha-3 dark:bg-n-alpha-2"
              />
            </div>
            <div>
              <h3 class="mb-1 text-heading-1 text-n-slate-12">
                {{
                  integration.name || $t('INTEGRATION_SETTINGS.LIXFOLLOW.TITLE')
                }}
              </h3>
              <p class="text-n-slate-11 text-body-main">
                {{ $t('INTEGRATION_SETTINGS.LIXFOLLOW.DESCRIPTION') }}
              </p>
            </div>
          </div>
          <div class="flex items-center mb-0">
            <Button
              v-if="!isConfigured"
              faded
              blue
              :label="$t('INTEGRATION_SETTINGS.CONNECT.BUTTON_TEXT')"
              @click="openConfigDialog"
            />
            <div v-else class="flex flex-col items-end gap-1.5">
              <div class="flex flex-wrap items-center gap-3">
                <span
                  class="inline-flex items-center gap-2 rounded-full px-3 py-1.5 border shadow-sm"
                  :class="connectionBadgeClass"
                >
                  <span
                    class="h-2.5 w-2.5 rounded-full shrink-0"
                    :class="connectionDotClass"
                  />
                  <span
                    class="text-xs font-semibold tracking-wide"
                    :class="connectionTextClass"
                  >
                    {{ connectionLabel }}
                  </span>
                </span>
                <Button
                  size="small"
                  color-scheme="secondary"
                  :label="
                    $t('INTEGRATION_SETTINGS.LIXFOLLOW.CONNECTION.TEST_BUTTON')
                  "
                  :is-loading="isTesting"
                  @click="testConnection"
                />
              </div>
              <span
                v-if="connectionHint"
                class="text-xs text-n-slate-11 max-w-sm text-right"
              >
                {{ connectionHint }}
              </span>
            </div>
          </div>
        </div>

        <!-- Connection settings -->
        <div
          v-if="isConfigured"
          class="flex flex-col gap-4 rounded-xl outline outline-1 -outline-offset-1 outline-n-weak bg-n-alpha-1 p-6"
        >
          <div class="flex items-center">
            <h3 class="text-heading-3 text-n-slate-12">
              {{ $t('INTEGRATION_SETTINGS.LIXFOLLOW.CONNECTION.TITLE') }}
            </h3>
          </div>

          <LixfollowConnectionFields
            v-model:api-url="apiUrl"
            v-model:token="lixfollowAccessToken"
            :has-saved-token="hasSavedAccessToken"
          />

          <div
            v-if="chatwootAccountId"
            class="flex flex-col gap-1 rounded-lg bg-n-alpha-2 px-4 py-3 text-xs text-n-slate-11"
          >
            <span class="font-semibold uppercase tracking-wide text-n-slate-10">
              {{
                $t('INTEGRATION_SETTINGS.LIXFOLLOW.CONNECTION.SURROGATE.LABEL')
              }}
            </span>
            <span class="font-mono break-all text-n-slate-12">
              {{ chatwootAccountId }}
            </span>
            <span class="text-n-slate-10">
              {{
                $t('INTEGRATION_SETTINGS.LIXFOLLOW.CONNECTION.SURROGATE.HELP')
              }}
            </span>
          </div>
        </div>

        <div v-if="isConfigured" class="flex justify-end">
          <Button
            :label="$t('INTEGRATION_SETTINGS.LIXFOLLOW.SAVE')"
            :is-loading="isSaving"
            @click="saveSettings"
          />
        </div>
      </div>
    </template>
  </SettingsLayout>

  <Dialog
    ref="configDialogRef"
    :title="$t('INTEGRATION_SETTINGS.LIXFOLLOW.CONNECTION.TITLE')"
    :confirm-button-label="$t('INTEGRATION_SETTINGS.LIXFOLLOW.SAVE')"
    :is-loading="isSaving"
    @confirm="saveSettingsFromDialog"
  >
    <div class="flex flex-col gap-4">
      <LixfollowConnectionFields
        v-model:api-url="apiUrl"
        v-model:token="lixfollowAccessToken"
        :has-saved-token="hasSavedAccessToken"
      />
    </div>
  </Dialog>
</template>
